// Audio context
let audioCtx = null;

// Background music state
let musicPlaying = false;
let musicNodes = [];
let musicInterval = null;
let currentMusicLevel = 1;

// Initialize audio system
export function initAudio() {
    if (audioCtx) return audioCtx;

    try {
        audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        console.log('Audio system initialized');
    } catch (e) {
        console.warn('Audio not available:', e);
    }

    return audioCtx;
}

// Get audio context
export function getAudioContext() {
    return audioCtx;
}

// Resume audio context if suspended
function resumeAudio() {
    if (audioCtx && audioCtx.state === 'suspended') {
        audioCtx.resume();
    }
}

// Play a sound effect
export function playSound(type) {
    if (!audioCtx) {
        initAudio();
    }
    if (!audioCtx) return;

    resumeAudio();

    switch(type) {
        case 'jump':
            playTone(400, 0.1, 'sine', 0.3, 600);
            break;
        case 'attack':
            playNoise(0.1, 0.5);
            playTone(200, 0.1, 'sawtooth', 0.2);
            break;
        case 'hit':
            playTone(300, 0.15, 'square', 0.3, 100);
            playNoise(0.08, 0.4);
            break;
        case 'damage':
            playTone(200, 0.2, 'sawtooth', 0.4, 100);
            playTone(150, 0.3, 'sine', 0.2);
            break;
        case 'bossAttack':
            playTone(80, 0.3, 'sawtooth', 0.4, 60);
            playNoise(0.2, 0.3);
            break;
        case 'bossCharge':
            playTone(100, 0.5, 'sine', 0.2, 300);
            break;
        case 'dodge':
            playTone(500, 0.1, 'sine', 0.2, 800);
            break;
        case 'victory':
            playMelody([523, 659, 784, 1047], 0.15, 0.3);
            break;
        case 'defeat':
            playMelody([400, 350, 300, 200], 0.2, 0.3);
            break;
        case 'levelUp':
            playMelody([400, 500, 600, 800], 0.12, 0.4);
            break;
        case 'menuSelect':
            playTone(600, 0.08, 'sine', 0.25, 800);
            break;
        case 'menuMove':
            playTone(400, 0.05, 'sine', 0.15, 450);
            break;
    }
}

// Play a tone
function playTone(freq, duration, type = 'sine', volume = 0.3, endFreq = null) {
    if (!audioCtx) return;

    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();

    osc.type = type;
    osc.frequency.setValueAtTime(freq, audioCtx.currentTime);
    if (endFreq) {
        osc.frequency.exponentialRampToValueAtTime(endFreq, audioCtx.currentTime + duration);
    }

    gain.gain.setValueAtTime(volume, audioCtx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + duration);

    osc.connect(gain);
    gain.connect(audioCtx.destination);

    osc.start();
    osc.stop(audioCtx.currentTime + duration);
}

// Play noise (for impact sounds)
function playNoise(duration, volume = 0.3) {
    if (!audioCtx) return;

    const bufferSize = audioCtx.sampleRate * duration;
    const buffer = audioCtx.createBuffer(1, bufferSize, audioCtx.sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < bufferSize; i++) {
        data[i] = (Math.random() * 2 - 1) * volume;
    }

    const noise = audioCtx.createBufferSource();
    const gain = audioCtx.createGain();

    noise.buffer = buffer;
    gain.gain.setValueAtTime(volume, audioCtx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + duration);

    noise.connect(gain);
    gain.connect(audioCtx.destination);

    noise.start();
}

// Play a melody (sequence of notes)
function playMelody(notes, noteDuration, volume) {
    notes.forEach((freq, i) => {
        setTimeout(() => {
            playTone(freq, noteDuration * 1.5, 'sine', volume);
        }, i * noteDuration * 1000);
    });
}

// Setup audio initialization on user interaction
export function setupAudioInit() {
    const tryInitAudio = () => {
        if (!audioCtx) initAudio();
    };
    document.addEventListener('click', tryInitAudio);
    document.addEventListener('keydown', tryInitAudio);
    document.addEventListener('gamepadconnected', tryInitAudio);
}

// Start background music for a level
export function startMusic(level = 1) {
    if (!audioCtx) initAudio();
    if (!audioCtx) return;

    // Stop any existing music
    stopMusic();

    currentMusicLevel = level;
    musicPlaying = true;

    // Music parameters based on level
    const musicConfig = getMusicConfig(level);

    // Create bass drone
    const bassOsc = audioCtx.createOscillator();
    const bassGain = audioCtx.createGain();
    bassOsc.type = 'sine';
    bassOsc.frequency.value = musicConfig.bassNote;
    bassGain.gain.value = 0.35;
    bassOsc.connect(bassGain);
    bassGain.connect(audioCtx.destination);
    bassOsc.start();
    musicNodes.push({ osc: bassOsc, gain: bassGain });

    // Create second bass (fifth)
    const bass2Osc = audioCtx.createOscillator();
    const bass2Gain = audioCtx.createGain();
    bass2Osc.type = 'sine';
    bass2Osc.frequency.value = musicConfig.bassNote * 1.5;
    bass2Gain.gain.value = 0.2;
    bass2Osc.connect(bass2Gain);
    bass2Gain.connect(audioCtx.destination);
    bass2Osc.start();
    musicNodes.push({ osc: bass2Osc, gain: bass2Gain });

    // Create rhythmic pulse
    let beat = 0;
    const bpm = musicConfig.bpm;
    const beatInterval = 60000 / bpm;

    musicInterval = setInterval(() => {
        if (!musicPlaying || !audioCtx) {
            clearInterval(musicInterval);
            return;
        }

        // Play drum beat
        playDrum(beat % 4 === 0 ? 'kick' : (beat % 2 === 0 ? 'snare' : 'hat'), musicConfig.drumVolume);

        // Play melody note every few beats
        if (beat % musicConfig.melodyInterval === 0) {
            const noteIndex = Math.floor(beat / musicConfig.melodyInterval) % musicConfig.melody.length;
            const note = musicConfig.melody[noteIndex];
            playMusicNote(note, musicConfig.noteDuration, musicConfig.noteType, musicConfig.noteVolume);
        }

        // Arpeggiate chord on some beats
        if (beat % 4 === 2) {
            const arpNote = musicConfig.arpNotes[beat % musicConfig.arpNotes.length];
            playMusicNote(arpNote, 0.15, 'triangle', 0.2);
        }

        beat++;
    }, beatInterval);
}

// Get music configuration for a level
function getMusicConfig(level) {
    const configs = {
        1: { // Shadow Demon - Dark, mysterious
            bassNote: 55, bpm: 100, drumVolume: 0.3,
            melody: [220, 262, 220, 196, 220, 262, 294, 262],
            melodyInterval: 4, noteDuration: 0.3, noteType: 'sine', noteVolume: 0.3,
            arpNotes: [330, 392, 440, 392]
        },
        2: { // Frost Giant - Cold, slow
            bassNote: 65, bpm: 85, drumVolume: 0.25,
            melody: [330, 392, 440, 392, 330, 294, 330, 392],
            melodyInterval: 4, noteDuration: 0.4, noteType: 'sine', noteVolume: 0.25,
            arpNotes: [494, 440, 392, 440]
        },
        3: { // Inferno King - Aggressive, fast
            bassNote: 50, bpm: 130, drumVolume: 0.35,
            melody: [196, 233, 262, 294, 262, 233, 196, 175],
            melodyInterval: 2, noteDuration: 0.2, noteType: 'sawtooth', noteVolume: 0.25,
            arpNotes: [392, 440, 494, 523]
        },
        4: { // Storm Titan - Electric, intense
            bassNote: 58, bpm: 120, drumVolume: 0.3,
            melody: [294, 350, 392, 440, 392, 350, 294, 262],
            melodyInterval: 3, noteDuration: 0.25, noteType: 'square', noteVolume: 0.2,
            arpNotes: [523, 494, 440, 392]
        },
        5: { // Void Emperor - Eerie, dissonant
            bassNote: 45, bpm: 90, drumVolume: 0.25,
            melody: [175, 185, 196, 208, 196, 185, 175, 165],
            melodyInterval: 4, noteDuration: 0.35, noteType: 'sine', noteVolume: 0.3,
            arpNotes: [262, 277, 294, 311]
        },
        6: { // Crystal Golem - Shimmering, magical
            bassNote: 62, bpm: 95, drumVolume: 0.25,
            melody: [392, 440, 523, 587, 523, 440, 392, 350],
            melodyInterval: 4, noteDuration: 0.3, noteType: 'sine', noteVolume: 0.28,
            arpNotes: [659, 587, 523, 494]
        },
        7: { // Jungle Beast - Tribal, primal
            bassNote: 55, bpm: 115, drumVolume: 0.35,
            melody: [220, 262, 294, 262, 220, 196, 175, 196],
            melodyInterval: 2, noteDuration: 0.2, noteType: 'triangle', noteVolume: 0.3,
            arpNotes: [330, 392, 440, 392]
        },
        8: { // Ocean Kraken - Deep, flowing
            bassNote: 49, bpm: 80, drumVolume: 0.2,
            melody: [196, 220, 262, 294, 330, 294, 262, 220],
            melodyInterval: 4, noteDuration: 0.4, noteType: 'sine', noteVolume: 0.25,
            arpNotes: [392, 440, 494, 523]
        },
        9: { // Solar Phoenix - Epic, triumphant
            bassNote: 65, bpm: 125, drumVolume: 0.32,
            melody: [392, 440, 523, 587, 659, 587, 523, 440],
            melodyInterval: 2, noteDuration: 0.25, noteType: 'sawtooth', noteVolume: 0.22,
            arpNotes: [784, 698, 659, 587]
        },
        10: { // Nightmare Lord - Dark, chaotic
            bassNote: 41, bpm: 140, drumVolume: 0.38,
            melody: [165, 175, 196, 208, 220, 208, 196, 175],
            melodyInterval: 2, noteDuration: 0.18, noteType: 'sawtooth', noteVolume: 0.25,
            arpNotes: [330, 311, 294, 277]
        }
    };

    return configs[level] || configs[1];
}

// Play a music note
function playMusicNote(freq, duration, type, volume) {
    if (!audioCtx || !musicPlaying) return;

    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();

    osc.type = type;
    osc.frequency.value = freq;

    gain.gain.setValueAtTime(0, audioCtx.currentTime);
    gain.gain.linearRampToValueAtTime(volume, audioCtx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + duration);

    osc.connect(gain);
    gain.connect(audioCtx.destination);

    osc.start();
    osc.stop(audioCtx.currentTime + duration + 0.1);
}

// Play drum sound
function playDrum(type, volume = 0.25) {
    if (!audioCtx || !musicPlaying) return;

    if (type === 'kick') {
        const osc = audioCtx.createOscillator();
        const gain = audioCtx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(150, audioCtx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(40, audioCtx.currentTime + 0.1);
        gain.gain.setValueAtTime(volume * 2, audioCtx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.15);
        osc.connect(gain);
        gain.connect(audioCtx.destination);
        osc.start();
        osc.stop(audioCtx.currentTime + 0.15);
    } else if (type === 'snare') {
        // Noise burst for snare
        const bufferSize = audioCtx.sampleRate * 0.1;
        const buffer = audioCtx.createBuffer(1, bufferSize, audioCtx.sampleRate);
        const data = buffer.getChannelData(0);
        for (let i = 0; i < bufferSize; i++) {
            data[i] = (Math.random() * 2 - 1);
        }
        const noise = audioCtx.createBufferSource();
        const gain = audioCtx.createGain();
        noise.buffer = buffer;
        gain.gain.setValueAtTime(volume * 1.5, audioCtx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.1);
        noise.connect(gain);
        gain.connect(audioCtx.destination);
        noise.start();
    } else if (type === 'hat') {
        // High frequency noise for hi-hat
        const bufferSize = audioCtx.sampleRate * 0.05;
        const buffer = audioCtx.createBuffer(1, bufferSize, audioCtx.sampleRate);
        const data = buffer.getChannelData(0);
        for (let i = 0; i < bufferSize; i++) {
            data[i] = (Math.random() * 2 - 1) * 0.5;
        }
        const noise = audioCtx.createBufferSource();
        const gain = audioCtx.createGain();
        const filter = audioCtx.createBiquadFilter();
        filter.type = 'highpass';
        filter.frequency.value = 8000;
        noise.buffer = buffer;
        gain.gain.setValueAtTime(volume, audioCtx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.05);
        noise.connect(filter);
        filter.connect(gain);
        gain.connect(audioCtx.destination);
        noise.start();
    }
}

// Stop background music
export function stopMusic() {
    musicPlaying = false;

    // Stop all music oscillators
    musicNodes.forEach(node => {
        try {
            if (node.osc) {
                node.osc.stop();
                node.osc.disconnect();
            }
            if (node.gain) {
                node.gain.disconnect();
            }
        } catch (e) {
            // Ignore errors from already stopped oscillators
        }
    });
    musicNodes = [];

    // Clear interval
    if (musicInterval) {
        clearInterval(musicInterval);
        musicInterval = null;
    }
}

// Check if music is playing
export function isMusicPlaying() {
    return musicPlaying;
}
